import React, { useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { FailurePanel } from "components/notifications";
import { RedirectPanel } from "components/navigation";
import { useQuery } from "hooks/useQuery";
import { EmployeeAssessmentsService } from "services";
import { UnauthenticatedPage } from "pages/layouts";
import { redirect } from "utils/redirect";

// The path Microservice.Employee puts in the emailed link. Both sides of that contract must change
// together - see Employee.Services/EmployeeAssessments/EmployeeAssessmentNotificationService.cs.
export const trainingLaunchRoutePath = "/assessments/launch";

const viewStates = {
    loading: "loading",
    invalidLink: "invalidLink",
    cannotOpen: "cannotOpen",
    providerUnavailable: "providerUnavailable",
    genericFailure: "genericFailure"
};

const forbiddenCode = 403;
const badRequestCode = 400;
const badGatewayCode = 502;

const loginTokenParameter = "login_token";

const identifierPattern = /^\d+$/;
const codePattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// A rejected launch call must never reveal which of the three link values failed to match, so every
// mismatch the API reports as 403 lands on the same invalid-link view the missing-value path uses.
function mapErrorToViewState(error) {
    let status = null;

    if (error != null && error.response != null) {
        status = error.response.status;
    }

    if (status === forbiddenCode) {
        return viewStates.invalidLink;
    }

    if (status === badRequestCode) {
        return viewStates.cannotOpen;
    }

    if (status === badGatewayCode) {
        return viewStates.providerUnavailable;
    }

    return viewStates.genericFailure;
}

// The API binds employee and training as int and code as Guid on an [ApiController], so a value
// mangled in transit fails model binding and returns 400 - which the page words as a fault to raise
// with a manager. Checking the shapes here keeps a mangled link on the invalid-link advice, which is
// what the driver can actually act on. The code shape is the hyphenated form the notification
// service emits.
function isLinkWellFormed(employee, training, code) {
    return identifierPattern.test(employee)
        && identifierPattern.test(training)
        && codePattern.test(code);
}

export function EmployeeAssessmentsTrainingLaunch() {
    const query = useQuery();
    const employee = query.get("employee");
    const training = query.get("training");
    const code = query.get("code");
    const [viewState, setViewState] = useState(viewStates.loading);

    useEffect(() => {
        if (!isLinkWellFormed(employee, training, code)) {
            setViewState(viewStates.invalidLink);
            return;
        }

        EmployeeAssessmentsService.getTrainingLaunch(employee, training, code).then(
            (result) => {
                openTraining(result);
            },
            (error) => {
                setViewState(mapErrorToViewState(error));
            }
        );
    }, []);

    // The sign-in token is held only for the length of the hand-off and is never stored or rendered.
    // redirect refuses any scheme other than https and throws, so a provider address that is not a
    // usable https URL surfaces as the cannot-be-opened view instead of escaping.
    function openTraining(result) {
        try {
            const trainingUrl = new URL(result.urlToTraining);
            trainingUrl.searchParams.set(loginTokenParameter, result.loginToken);
            redirect(trainingUrl.toString());
        } catch {
            setViewState(viewStates.cannotOpen);
        }
    }

    return (
        <UnauthenticatedPage>
            <ShowHide
                evaluator={viewState === viewStates.loading}
                show={
                    <RedirectPanel
                        heading="Opening your training"
                        message="Please wait while we take you to your training."
                    />
                }
            />
            <ShowHide
                evaluator={viewState === viewStates.invalidLink}
                show={
                    <FailurePanel title="This link is not valid">
                        <p>
                            This link is not valid or no longer applies.
                        </p>
                        <p>
                            Please use the most recent link from your email,
                            or get in touch with your manager/Vision
                            administrator.
                        </p>
                    </FailurePanel>
                }
            />
            <ShowHide
                evaluator={viewState === viewStates.cannotOpen}
                show={
                    <FailurePanel title="This training cannot be opened">
                        <p>
                            This training cannot be opened at the moment.
                        </p>
                        <p>
                            Please get in touch with your manager/Vision
                            administrator.
                        </p>
                    </FailurePanel>
                }
            />
            <ShowHide
                evaluator={viewState === viewStates.providerUnavailable}
                show={
                    <FailurePanel title="Training provider unavailable">
                        <p>
                            The training provider is currently unavailable.
                        </p>
                        <p>
                            Please try again later.
                        </p>
                    </FailurePanel>
                }
            />
            <ShowHide
                evaluator={viewState === viewStates.genericFailure}
                show={
                    <FailurePanel title="Something went wrong">
                        <p>
                            We could not open your training.
                        </p>
                        <p>
                            Please try again later, or get in touch with
                            your manager/Vision administrator.
                        </p>
                    </FailurePanel>
                }
            />
        </UnauthenticatedPage>
    );
}
